// RenderTargetArray.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { change } from 'redux-form';
import {
    faWindowClose,
    faPlusCircle,
    faRedo,
    faChartBar,
    faEye,
    faEyeSlash,
    faGripVertical,
    faChevronUp,
    faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import styles from './assets/program-array-fields.module.scss';
import FormTarget from './PBTargetForm';
import GeneralEnumService from '../../../../../enumerables/general-enum';
import { getValFromEnum } from '../../../../../components/redux-form/utils/dropdown-values';
import { Button, Modal } from 'react-bootstrap';
import Router from '../../../../../navigation/router';
import { routes } from '../../../../../navigation';
import { Link, useParams } from 'react-router-dom';
import { Droppable, Draggable, DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';

import { genCid, getNextSortOrder, stableKey } from './dnd/cid';
import { useRegisterDroppable } from './dnd/DndRegistry';

const RenderTargetArray = (props: any) => {
    const { fields, remove, isHiddenShow } = props;

    const prompts = useMemo(() => {
        const enumSet = GeneralEnumService ? GeneralEnumService.getEnum('ProgramPrompt') : {};
        const programPromptItems: SelectObject[] = getValFromEnum(enumSet);
        return programPromptItems.map((prompt: any) => ({
            id: prompt.value,
            title: prompt.label,
            prompt: prompt.value,
            checked: false,
        }));
    }, []);

    // unique droppable per each shortterm targets array
    const droppableId = `targets-${fields.name}`;
    useRegisterDroppable(droppableId, fields);

    return (
        <Droppable droppableId={droppableId} type="targets">
            {(dropProvided) => (
                <ul
                    className={classNames(styles.ul, remove ? styles.removeStyle : styles.targetWrap)}
                    ref={dropProvided.innerRef}
                    {...dropProvided.droppableProps}
                >
                    <li className={classNames(styles.addBtnLi, styles.addTargetBtnLi)}>
                        <button
                            type="button"
                            className={classNames('btn', 'btn-outline-info', 'mr-3', styles.addBtn)}
                            onClick={() =>
                                fields.push({
                                    clientId: genCid(),
                                    sortOrder: getNextSortOrder(fields),
                                    programTargetPrompts: prompts,
                                })
                            }
                        >
                            <FontAwesomeIcon icon={faPlusCircle} /> <span>Add target</span>
                        </button>
                    </li>

                    {fields.map((member: any, index: number, array: any) => {
                        const row = array.get(index);
                        // ensureCid(row);
                        const targetKey = stableKey(row, 'target');

                        return (
                            <Draggable draggableId={targetKey} index={index} key={targetKey}>
                                {(dragProvided, snapshot) => (
                                    <li
                                        ref={dragProvided.innerRef}
                                        {...dragProvided.draggableProps}
                                        style={dragProvided.draggableProps.style}
                                        className={classNames(
                                            'w-100',
                                            snapshot.isDragging ? styles.dragging : '',
                                        )}
                                    >
                                        <RenderTargetRow
                                            dragHandleProps={dragProvided.dragHandleProps}
                                            member={member}
                                            isHiddenShow={isHiddenShow}
                                            remove={remove}
                                            fields={array}
                                            index={index}
                                            isNotFirst={!!index}
                                        />
                                    </li>
                                )}
                            </Draggable>
                        );
                    })}

                    {dropProvided.placeholder}
                </ul>
            )}
        </Droppable>
    );
};

type RowProps = {
    member: any;
    index: number;
    fields: any;
    remove: boolean;
    isNotFirst: boolean;
    isHiddenShow: string;
    dragHandleProps?: DraggableProvidedDragHandleProps | null;
};

const RenderTargetRow: React.FC<RowProps> = ({
    member,
    index,
    fields,
    remove,
    isNotFirst,
    isHiddenShow,
    dragHandleProps,
}) => {
    const dispatch = useDispatch();
    const [changeSt, setChanged] = useState(false);
    const [openedDeleteTarget, setOpenedDeleteTarget] = useState(false);

    const row = fields.get(index);
    const [isHide, setHide] = useState(!!row?.isCollapsed);
    const [isHiddenLocal, setIsHiddenLocal] = useState(+row?.isHidden === 1);

    const { id } = useParams<{ id: string }>();

    const handleClose = () => setOpenedDeleteTarget(false);

    const deleteRow = () => {
        handleClose();
        if (!row?.id) {
            fields.remove(index);
        } else {
            dispatch(change('program-book', `${member}.status`, 2));
        }
        setTimeout(() => setChanged(!changeSt), 0);
    };

    const hideRow = () => {
        dispatch(change('program-book', `${member}.isHidden`, 1));
        setIsHiddenLocal(true);
    };

    const showRow = () => {
        dispatch(change('program-book', `${member}.isHidden`, 0));
        setIsHiddenLocal(false);
    };

    const backRow = () => {
        dispatch(change('program-book', `${member}.status`, 1));
        setTimeout(() => setChanged(!changeSt), 0);
    };

    const isApplied: boolean = +row?.status === 3;


    useEffect(() => {
        if (row && !row.prompt) {
            dispatch(change('program-book', `${member}.prompt`, 10));
        }
        // eslint-disable-next-line
    }, [row?.id]);

    if (isHiddenShow === '0' && isHiddenLocal) return null;

    const creditStyle = { display: 'none' as const };

    return (
        <div
            style={+row?.status === 2 ? creditStyle : {}}
            className={classNames(
                styles.wrappedFieldsTarget,
                styles.wrappedFieldsTargetCustom,
                isHiddenLocal ? styles.hiddenItem : '',
            )}
        >
            <FormTarget
                remove={remove}
                fields={fields}
                memberD={member}
                showOption={row?.type == 3 || row?.type?.value == 3}
                rowType={row?.type?.value ? row?.type?.value : row?.type}
                isMastered={row?.isMastered}
                isPhased={row?.isPhased}
                idTarget={row?.id}
                showAttempts={row?.type == 1 || row?.type?.value == 1}
                showPrompt={row?.type == 1 || row?.type?.value == 1}
                isCollapsed={isHide}
            />

            <div className={styles.actionBar} style={{ verticalAlign: 'top' }}>
                <button
                    type="button"
                    className={styles.actionBtn}
                    onClick={() => {
                        setHide(!isHide);
                        dispatch(change('program-book', `${member}.isCollapsed`, !isHide));
                    }}
                >
                    <FontAwesomeIcon icon={isHide ? faChevronDown : faChevronUp} />
                </button>
                <div {...(dragHandleProps || {})} className={styles.dragHandle} title="Click and drag to reorder">
                    <FontAwesomeIcon icon={faGripVertical} />
                </div>
                <Link
                    target="_blank"
                    className={styles.actionBtn}
                    title="See results"
                    to={Router.generate(routes.PROGRAM_BOOK_TARGET, {
                        id: id,
                        targetId: row?.id,
                    })}
                >
                    <FontAwesomeIcon icon={faChartBar} />
                </Link>
                {isHiddenLocal ? (
                    <button type="button" className={styles.actionBtnSuccess} title="Open Target" onClick={showRow}>
                        <FontAwesomeIcon icon={faEye} />
                    </button>
                ) : (
                    <button type="button" className={styles.actionBtnDanger} title="Close Target" onClick={hideRow}>
                        <FontAwesomeIcon icon={faEyeSlash} />
                    </button>
                )}

                {!isApplied && isNotFirst ? (
                    +row?.status !== 2 ? (
                        <>
                            <button
                                type="button"
                                className={styles.actionBtnDanger}
                                title="Remove Target"
                                onClick={() => (row?.id ? setOpenedDeleteTarget(10 + row.id) : deleteRow())}
                            >
                                <FontAwesomeIcon icon={faWindowClose} />
                            </button>
                            <Modal show={openedDeleteTarget === 10 + row?.id} onHide={handleClose}>
                                <Modal.Header closeButton>
                                    <Modal.Title>Confirmation</Modal.Title>
                                </Modal.Header>
                                <Modal.Body>
                                    Are you sure you want to delete the target? All data, collected for the target, will
                                    be lost.
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button variant="secondary" onClick={handleClose}>
                                        No
                                    </Button>
                                    <Button variant="success" onClick={deleteRow}>
                                        Yes
                                    </Button>
                                </Modal.Footer>
                            </Modal>
                        </>
                    ) : (
                        <button
                            type="button"
                            className={styles.actionBtnSuccess}
                            title="Restore Target"
                            onClick={backRow}
                        >
                            <FontAwesomeIcon icon={faRedo} />
                        </button>
                    )
                ) : null}
            </div>
        </div>
    );
};

export default RenderTargetArray;
